import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AlertCircle, Home } from 'lucide-react';
import DashboardHeader from '@/components/DashboardHeader';
import { withTranslation, WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class DashboardErrorBoundaryInner extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Dashboard error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  private handleHome = () => {
    window.location.href = '/dashboard';
  };

  public render() {
    const { t } = this.props;

    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-secondary/10">
          <DashboardHeader />
          
          <main className="container mx-auto px-4 py-8 pt-24">
            <Card className="max-w-2xl mx-auto p-6">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t('common:error_boundary.page_error')}</AlertTitle>
                <AlertDescription className="mt-2">
                  <p className="mb-4">
                    {this.state.error?.message || t('common:error_boundary.unexpected_error')}
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={this.handleReset} variant="outline" size="sm">
                      {t('common:error_boundary.try_again')}
                    </Button>
                    <Button onClick={this.handleHome} variant="default" size="sm">
                      <Home className="mr-2 h-4 w-4" />
                      {t('common:error_boundary.go_home')}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            </Card>
          </main>
        </div>
      );
    }

    return this.props.children;
  }
}

export const DashboardErrorBoundary = withTranslation('common')(DashboardErrorBoundaryInner);
